// スマートガイド（整列補助線）の純粋ロジック。
//
// 移動・複製中のオブジェクト（複数選択時は外接矩形）の端／中心を、他オブジェクトの
// 端／中心と突き合わせ、しきい値以内で最も近いラインへスナップする量と、表示すべき
// 補助線（ガイド）を算出する。PowerPoint / FigJam のスマートガイドに相当する。
//
// 座標はすべてステージ座標（px）。React / DOM へ依存しない純粋関数のためテストしやすい。

/** 矩形（左上 + 幅高さ）。回転は考慮せず、データモデルの x/y/w/h をそのまま扱う。 */
export interface AlignmentBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** 表示する補助線 1 本。axis="x" は縦線（x 固定）、axis="y" は横線（y 固定）。 */
export interface AlignmentGuide {
  readonly axis: "x" | "y";
  /** 線の固定座標（axis="x" なら x、axis="y" なら y）。 */
  readonly position: number;
  /** 線分の開始座標（axis="x" なら y、axis="y" なら x）。 */
  readonly start: number;
  /** 線分の終了座標。 */
  readonly end: number;
  /**
   * ガイドの種類。未指定 / "align" は端・中心の整列（実線）。"size" はリサイズ時のサイズ一致
   * （寸法一致）ガイドで、描画側は破線などで区別する。
   */
  readonly variant?: "align" | "size" | undefined;
}

/** スマートガイドのスナップ判定しきい値（画面 px）。ズームで割ってステージ px に換算して使う。 */
export const SNAP_THRESHOLD_SCREEN_PX = 6;

export interface AlignmentResult {
  /** x 方向へ加算するスナップ量（揃わなければ 0）。 */
  readonly snapDx: number;
  /** y 方向へ加算するスナップ量（揃わなければ 0）。 */
  readonly snapDy: number;
  /** x 方向で整列が成立したか（成立軸はグリッド吸着より優先する判定に使う）。 */
  readonly matchedX: boolean;
  /** y 方向で整列が成立したか。 */
  readonly matchedY: boolean;
  /** 表示すべき補助線一覧。 */
  readonly guides: readonly AlignmentGuide[];
}

// 補助線へ巻き込む相手オブジェクトを拾う許容差（ステージ px）。
// スナップ後はベストペアが完全一致するため、整列ラインを共有する他オブジェクトを
// 1 本のガイドへまとめるための小さな値。
const GUIDE_COINCIDENCE_PX = 0.5;

const EMPTY_RESULT: AlignmentResult = {
  snapDx: 0,
  snapDy: 0,
  matchedX: false,
  matchedY: false,
  guides: [],
};

/** 矩形の縦ライン（x 値）3 本: 左端 / 中央 / 右端。 */
function verticalLines(box: AlignmentBox): readonly number[] {
  return [box.x, box.x + box.w / 2, box.x + box.w];
}

/** 矩形の横ライン（y 値）3 本: 上端 / 中央 / 下端。 */
function horizontalLines(box: AlignmentBox): readonly number[] {
  return [box.y, box.y + box.h / 2, box.y + box.h];
}

/**
 * 1 軸分のベストスナップを求める。
 * movingLines（移動側 3 本）と各 static の lines を総当たりし、|static - moving| が threshold
 * 以内で最小となるペアを選ぶ。返り値の delta は移動側へ加算するスナップ量、position は
 * 揃える先のライン座標（= 補助線を出す位置）。候補が無ければ null。
 */
function bestSnap(
  movingLines: readonly number[],
  staticLineGroups: readonly (readonly number[])[],
  threshold: number
): { readonly delta: number; readonly position: number } | null {
  let best: { delta: number; position: number } | null = null;
  let bestAbs = Infinity;
  for (const movingLine of movingLines) {
    for (const lines of staticLineGroups) {
      for (const staticLine of lines) {
        const delta = staticLine - movingLine;
        const abs = Math.abs(delta);
        if (abs <= threshold && abs < bestAbs) {
          bestAbs = abs;
          best = { delta, position: staticLine };
        }
      }
    }
  }
  return best;
}

/**
 * スナップ後の整列ライン（position）を通る補助線 1 本を組み立てる。
 * 線分の伸びは、移動矩形と「position にライン（端 / 中心）を持つ全 static 矩形」の
 * 垂直方向（axis に直交する方向）の合併範囲とし、整列している全オブジェクトを貫く線にする。
 */
function buildGuide(
  axis: "x" | "y",
  movedBox: AlignmentBox,
  statics: readonly AlignmentBox[],
  position: number
): AlignmentGuide {
  let start = axis === "x" ? movedBox.y : movedBox.x;
  let end = axis === "x" ? movedBox.y + movedBox.h : movedBox.x + movedBox.w;
  for (const s of statics) {
    const lines = axis === "x" ? verticalLines(s) : horizontalLines(s);
    const onLine = lines.some((line) => Math.abs(line - position) <= GUIDE_COINCIDENCE_PX);
    if (!onLine) continue;
    const sStart = axis === "x" ? s.y : s.x;
    const sEnd = axis === "x" ? s.y + s.h : s.x + s.w;
    start = Math.min(start, sStart);
    end = Math.max(end, sEnd);
  }
  return { axis, position, start, end };
}

/**
 * 移動矩形 moving を statics へ整列させるスナップ量と補助線を求める。
 *
 * @param moving 移動・複製中の矩形（複数選択時は外接矩形）。
 * @param statics 整列対象の他オブジェクト矩形（移動対象自身は呼び出し側で除外しておく）。
 * @param threshold スナップ判定のしきい値（ステージ px）。通常は screen px / zoom。
 */
export function computeAlignmentSnap(
  moving: AlignmentBox,
  statics: readonly AlignmentBox[],
  threshold: number
): AlignmentResult {
  if (statics.length === 0 || threshold <= 0) return EMPTY_RESULT;

  const staticVertical = statics.map((s) => verticalLines(s));
  const staticHorizontal = statics.map((s) => horizontalLines(s));

  const snapX = bestSnap(verticalLines(moving), staticVertical, threshold);
  const snapY = bestSnap(horizontalLines(moving), staticHorizontal, threshold);

  if (!snapX && !snapY) return EMPTY_RESULT;

  const snapDx = snapX?.delta ?? 0;
  const snapDy = snapY?.delta ?? 0;
  // スナップ後の矩形位置でガイドの線分範囲を確定する。
  const movedBox: AlignmentBox = { ...moving, x: moving.x + snapDx, y: moving.y + snapDy };
  const guides: AlignmentGuide[] = [];
  if (snapX) guides.push(buildGuide("x", movedBox, statics, snapX.position));
  if (snapY) guides.push(buildGuide("y", movedBox, statics, snapY.position));

  return {
    snapDx,
    snapDy,
    matchedX: snapX !== null,
    matchedY: snapY !== null,
    guides,
  };
}

/** リサイズで動く辺（方位）。四隅は横 1・縦 1 の 2 辺、辺ハンドルはどちらか 1 辺が true。 */
export interface ResizeMovingEdges {
  readonly movesLeft: boolean;
  readonly movesRight: boolean;
  readonly movesTop: boolean;
  readonly movesBottom: boolean;
}

/** リサイズ対象のフレーム（左上 + 幅高さ）。 */
export interface ResizeFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface ResizeAlignmentResult {
  /** スナップ適用後のフレーム。 */
  readonly frame: ResizeFrame;
  /** x 軸（幅・左右辺）でスナップが成立したか。グリッド吸着より整列を優先する判定に使う。 */
  readonly matchedX: boolean;
  /** y 軸（高さ・上下辺）でスナップが成立したか。 */
  readonly matchedY: boolean;
  /** 表示すべき補助線一覧。 */
  readonly guides: readonly AlignmentGuide[];
}

/** 1 軸分のサイズ一致（寸法一致）ベストマッチを求める。候補が無ければ null。 */
function bestSizeMatch(
  movingSize: number,
  statics: readonly AlignmentBox[],
  axis: "x" | "y",
  threshold: number
): { readonly delta: number; readonly matched: AlignmentBox } | null {
  let best: { delta: number; matched: AlignmentBox } | null = null;
  let bestAbs = Infinity;
  for (const s of statics) {
    const size = axis === "x" ? s.w : s.h;
    const delta = size - movingSize;
    const abs = Math.abs(delta);
    if (abs <= threshold && abs < bestAbs) {
      bestAbs = abs;
      best = { delta, matched: s };
    }
  }
  return best;
}

/**
 * サイズ一致ガイド（寸法一致）を組み立てる。リサイズ後のフレームと一致した相手矩形の、
 * リサイズ軸に沿った両端（幅一致なら左右辺、高さ一致なら上下辺）へ variant="size" の線を出し、
 * 「この 2 つが同じ幅（高さ）である」ことを示す。各線は自分の矩形の直交方向の全長に伸ばす。
 */
function buildSizeGuides(
  axis: "x" | "y",
  resized: AlignmentBox,
  matched: AlignmentBox
): AlignmentGuide[] {
  const linesFor = (box: AlignmentBox): AlignmentGuide[] => {
    if (axis === "x") {
      const start = box.y;
      const end = box.y + box.h;
      return [
        { axis: "x", position: box.x, start, end, variant: "size" },
        { axis: "x", position: box.x + box.w, start, end, variant: "size" },
      ];
    }
    const start = box.x;
    const end = box.x + box.w;
    return [
      { axis: "y", position: box.y, start, end, variant: "size" },
      { axis: "y", position: box.y + box.h, start, end, variant: "size" },
    ];
  };
  return [...linesFor(resized), ...linesFor(matched)];
}

/**
 * リサイズ中のフレームを statics へ整列させるスナップ量と補助線を求める。
 *
 * 各軸で 2 種類の候補を評価し、|delta| が小さい方を採用する（同値は辺整列を優先）。
 * 1. 辺整列（先端スナップ）: 動かしている辺（右辺リサイズなら右辺）を、他図形の端・中心ラインへ揃える。
 *    アンカー辺（反対側）は固定し、可動辺のみ動かす。ガイドは整列ライン 1 本（variant="align"）。
 * 2. サイズ一致（後端ガイド）: 幅 / 高さを他図形の幅 / 高さへ一致させる。可動辺のみ動かして
 *    アンカー辺を固定し、リサイズ対象と一致相手の両端へ variant="size" のガイドを出す
 *    （「後ろにも赤い線」を満たす）。
 *
 * @param frame リサイズ後の候補フレーム（clamp 前）。
 * @param edges 動かしている辺（use-resize-handle の movesLeft/Right/Top/Bottom）。
 * @param statics 整列対象の他オブジェクト矩形（回転・移動対象は呼び出し側で除外済み）。
 * @param threshold スナップ判定しきい値（ステージ px。通常は screen px / zoom）。
 */
export function computeResizeAlignment(
  frame: ResizeFrame,
  edges: ResizeMovingEdges,
  statics: readonly AlignmentBox[],
  threshold: number
): ResizeAlignmentResult {
  if (statics.length === 0 || threshold <= 0) {
    return { frame, matchedX: false, matchedY: false, guides: [] };
  }

  let { x, y, w, h } = frame;
  let matchedX = false;
  let matchedY = false;
  // ガイドの構築は両軸のスナップ確定後に「最終フレーム」から一括で行う。X 軸を先に解決した時点で
  // 縦ガイドを組むと、その後 Y 軸で h/y が変わったとき縦ガイドの線分長（直交方向）が最終枠とずれる
  // （四隅リサイズで両軸同時スナップ時）。ここでは「どのガイドを出すか」だけ記録しておく。
  type GuideIntent =
    | { readonly kind: "edge"; readonly position: number }
    | { readonly kind: "size"; readonly matched: AlignmentBox };
  let xGuide: GuideIntent | null = null;
  let yGuide: GuideIntent | null = null;

  // ---- X 軸（幅・左右辺）----
  if (edges.movesLeft || edges.movesRight) {
    const movingEdgeX = edges.movesRight ? x + w : x;
    const edgeSnap = bestSnap(
      [movingEdgeX],
      statics.map((s) => verticalLines(s)),
      threshold
    );
    const sizeSnap = bestSizeMatch(w, statics, "x", threshold);
    const edgeAbs = edgeSnap ? Math.abs(edgeSnap.delta) : Infinity;
    const sizeAbs = sizeSnap ? Math.abs(sizeSnap.delta) : Infinity;
    if (edgeSnap && edgeAbs <= sizeAbs) {
      // 辺整列: 可動辺のみ delta 分動かす（アンカー辺は固定）。
      if (edges.movesRight) {
        w = w + edgeSnap.delta;
      } else {
        x = x + edgeSnap.delta;
        w = w - edgeSnap.delta;
      }
      matchedX = true;
      xGuide = { kind: "edge", position: edgeSnap.position };
    } else if (sizeSnap) {
      // サイズ一致: 幅を相手へ合わせ、可動辺側のみ動かす。
      const newW = sizeSnap.matched.w;
      if (edges.movesRight) {
        w = newW;
      } else {
        const rightEdge = x + w;
        w = newW;
        x = rightEdge - newW;
      }
      matchedX = true;
      xGuide = { kind: "size", matched: sizeSnap.matched };
    }
  }

  // ---- Y 軸（高さ・上下辺）----
  if (edges.movesTop || edges.movesBottom) {
    const movingEdgeY = edges.movesBottom ? y + h : y;
    const edgeSnap = bestSnap(
      [movingEdgeY],
      statics.map((s) => horizontalLines(s)),
      threshold
    );
    const sizeSnap = bestSizeMatch(h, statics, "y", threshold);
    const edgeAbs = edgeSnap ? Math.abs(edgeSnap.delta) : Infinity;
    const sizeAbs = sizeSnap ? Math.abs(sizeSnap.delta) : Infinity;
    if (edgeSnap && edgeAbs <= sizeAbs) {
      if (edges.movesBottom) {
        h = h + edgeSnap.delta;
      } else {
        y = y + edgeSnap.delta;
        h = h - edgeSnap.delta;
      }
      matchedY = true;
      yGuide = { kind: "edge", position: edgeSnap.position };
    } else if (sizeSnap) {
      const newH = sizeSnap.matched.h;
      if (edges.movesBottom) {
        h = newH;
      } else {
        const bottomEdge = y + h;
        h = newH;
        y = bottomEdge - newH;
      }
      matchedY = true;
      yGuide = { kind: "size", matched: sizeSnap.matched };
    }
  }

  // 両軸確定後の最終フレームからガイドを構築する（線分長が最終枠と一致する）。
  const finalFrame: AlignmentBox = { x, y, w, h };
  const guides: AlignmentGuide[] = [];
  if (xGuide?.kind === "edge") guides.push(buildGuide("x", finalFrame, statics, xGuide.position));
  else if (xGuide?.kind === "size") guides.push(...buildSizeGuides("x", finalFrame, xGuide.matched));
  if (yGuide?.kind === "edge") guides.push(buildGuide("y", finalFrame, statics, yGuide.position));
  else if (yGuide?.kind === "size") guides.push(...buildSizeGuides("y", finalFrame, yGuide.matched));

  return { frame: finalFrame, matchedX, matchedY, guides };
}
